import { RefObject, useEffect, useRef } from "react";

import {
  ServiceRequestPrefill,
  frameOrigin,
  initMessage,
  pageUriFor,
  readFrameMessage,
} from "./serviceRequestFrame";

interface Options {
  iframeRef: RefObject<HTMLIFrameElement>;
  url: string;
  prefill: ServiceRequestPrefill;
  active: boolean;
  onReady?: () => void;
  onDone?: () => void;
}

// The parent side of the service request form's postMessage handshake (see
// serviceRequestFrame.ts): answers fp-consult-ready with the page info and
// prefill, forwards the form's analytics events to GTM, and reports when the
// request was submitted.
export function useServiceRequestFrame({
  iframeRef,
  url,
  prefill,
  active,
  onReady,
  onDone,
}: Options): void {
  // Latest values without re-subscribing on every render.
  const latest = useRef({ prefill, onReady, onDone });
  latest.current = { prefill, onReady, onDone };

  useEffect(() => {
    if (!active) return;
    const origin = frameOrigin(url);

    const onMessage = (event: MessageEvent) => {
      const frame = iframeRef.current?.contentWindow;
      const msg = readFrameMessage(event, origin, frame);
      if (!msg || !frame) return;

      if (msg.kind === "ready") {
        frame.postMessage(
          initMessage(latest.current.prefill, {
            pageUri: pageUriFor(window.location),
            pageName: document.title,
          }),
          origin
        );
        latest.current.onReady?.();
      } else if (msg.kind === "done") {
        latest.current.onDone?.();
      } else if (msg.kind === "event") {
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ ...msg.eventData, event: msg.eventName });
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [active, url, iframeRef]);
}
